import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import DataTable from "../components/common/Table";
import Modal, { FIELD_CLASS, LABEL_CLASS } from "../components/common/Modal";
import ConfirmModal from "../components/common/ConfirmModal";
import useStore from "../store/useStore";
import api from "../services/api";
import {
  COUNTRY_CODE_OPTIONS,
  splitPhoneWithCountryCode,
  buildPhoneWithCountryCode,
  getCountryCodeOption
} from "../utils/phoneCountryCodes";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  countryCode: "+220",
  phone: ""
};

const splitTenantName = (tenant) => {
  if (tenant.firstName || tenant.lastName) {
    return {
      firstName: tenant.firstName || "",
      lastName: tenant.lastName || ""
    };
  }

  const nameParts = (tenant.tenantName || "").split(" ").filter(Boolean);
  return {
    firstName: nameParts[0] || "",
    lastName: nameParts.slice(1).join(" ") || ""
  };
};

const Tenants = () => {
  const { toast } = useStore();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/tenants");
      setTenants(res.data.data || []);
    } catch (error) {
      console.error("Error fetching tenants:", error);
      toast("Failed to load tenants", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const closeForm = () => {
    setForm(emptyForm);
    setSelectedTenant(null);
    setFormOpen(false);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setSelectedTenant(null);
    setFormOpen(true);
  };

  const openEdit = (tenant) => {
    const names = splitTenantName(tenant);
    const parsedPhone = splitPhoneWithCountryCode(tenant.phone || "", "+220");

    setSelectedTenant(tenant);
    setForm({
      firstName: names.firstName,
      lastName: names.lastName,
      email: tenant.email || "",
      countryCode: parsedPhone.countryCode,
      phone: parsedPhone.number
    });
    setFormOpen(true);
  };

  const handleAdd = async () => {
    if (!form.firstName || !form.firstName.trim()) {
      toast("First name is required", "error");
      return;
    }

    if (!form.lastName || !form.lastName.trim()) {
      toast("Last name is required", "error");
      return;
    }

    if (!form.phone || !form.phone.trim()) {
      toast("Phone number is required", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        tenantName: `${form.firstName.trim()} ${form.lastName.trim()}`,
        email: form.email?.trim() || null,
        phone: buildPhoneWithCountryCode(form.countryCode, form.phone)
      };

      const response = await api.post("/tenants", payload);
      toast(response.data?.message || "Tenant added successfully", "success");
      closeForm();
      fetchTenants();
    } catch (error) {
      console.error("Error adding tenant:", error);
      toast(error.response?.data?.message || "Failed to add tenant", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedTenant) {
      toast("Please select a tenant to update", "error");
      return;
    }

    if (!form.firstName || !form.firstName.trim()) {
      toast("First name is required", "error");
      return;
    }

    if (!form.phone || !form.phone.trim()) {
      toast("Phone number is required", "error");
      return;
    }

    try {
      setLoading(true);
      const firstName = form.firstName.trim();
      const lastName = form.lastName.trim();
      const tenantName = lastName ? `${firstName} ${lastName}` : firstName;

      const payload = {
        tenantName,
        email: form.email?.trim() || null,
        phone: buildPhoneWithCountryCode(form.countryCode, form.phone)
      };

      await api.put(`/tenants/${selectedTenant.id}`, payload);
      toast("Tenant updated successfully", "success");
      closeForm();
      fetchTenants();
    } catch (error) {
      console.error("Error updating tenant:", error);
      toast(error.response?.data?.message || "Failed to update tenant", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      await api.delete(`/tenants/${deleteTarget.id}`);
      toast("Tenant deleted successfully", "success");
      setDeleteTarget(null);
      fetchTenants();
    } catch (error) {
      console.error("Error deleting tenant:", error);
      toast("Failed to delete tenant", "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = tenants.filter((tenant) => {
    const names = splitTenantName(tenant);
    const term = search.toLowerCase();
    return (
      names.firstName.toLowerCase().includes(term) ||
      names.lastName.toLowerCase().includes(term) ||
      (tenant.tenantName || "").toLowerCase().includes(term) ||
      (tenant.email || "").toLowerCase().includes(term) ||
      (tenant.phone || "").toLowerCase().includes(term)
    );
  });

  const selectedCountry = getCountryCodeOption(form.countryCode);

  return (
    <div>
      <PageHeader
        title="Tenants"
        action={
          <Button variant="primary" icon="add" onClick={openCreate}>
            Add Tenant
          </Button>
        }
      />

      <DataTable
        columns={[
          {
            key: "firstName",
            label: "First Name",
            sortValue: (row) => splitTenantName(row).firstName,
            render: (row) => splitTenantName(row).firstName || "-"
          },
          {
            key: "lastName",
            label: "Last Name",
            sortValue: (row) => splitTenantName(row).lastName,
            render: (row) => splitTenantName(row).lastName || "-"
          },
          { key: "email", label: "Email", render: (row) => row.email || "-" },
          { key: "phone", label: "Phone Number", render: (row) => row.phone || "-" }
        ]}
        rows={filtered}
        loading={loading && !formOpen && !deleteTarget}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search by name, email or phone..."
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        emptyMessage='No tenants found. Click "Add Tenant" to create one.'
      />

      <Modal
        open={formOpen}
        title={selectedTenant ? "Update Tenant" : "Add Tenant"}
        onClose={closeForm}
        footer={
          <>
            <Button variant="secondary" onClick={closeForm} disabled={loading}>
              Cancel
            </Button>
            {selectedTenant ? (
              <Button variant="success" icon="update" onClick={handleUpdate} disabled={loading}>
                Update
              </Button>
            ) : (
              <Button variant="primary" icon="add" onClick={handleAdd} disabled={loading}>
                Add
              </Button>
            )}
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className={LABEL_CLASS}>
              First Name <span className="text-red-500">*</span>
            </label>
            <input type="text" name="firstName" value={form.firstName} onChange={handleChange} className={FIELD_CLASS} />
          </div>
          <div>
            <label className={LABEL_CLASS}>Last Name</label>
            <input type="text" name="lastName" value={form.lastName} onChange={handleChange} className={FIELD_CLASS} />
          </div>
          <div>
            <label className={LABEL_CLASS}>Email</label>
            <input type="email" name="email" value={form.email} onChange={handleChange} className={FIELD_CLASS} />
          </div>
          <div>
            <label className={LABEL_CLASS}>
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              <select name="countryCode" value={form.countryCode} onChange={handleChange} className={FIELD_CLASS}>
                {COUNTRY_CODE_OPTIONS.map((item) => (
                  <option key={item.code} value={item.code}>{item.label}</option>
                ))}
              </select>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Phone number"
                className={`col-span-2 ${FIELD_CLASS}`}
              />
            </div>
            <div className="mt-2">
              <span className="inline-flex items-center rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 border border-teal-200">
                {selectedCountry?.label}
              </span>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        message={
          deleteTarget
            ? `Are you sure you want to delete tenant "${splitTenantName(deleteTarget).firstName} ${splitTenantName(deleteTarget).lastName}"?`
            : ""
        }
        loading={loading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Tenants;
